// ---------------------------------------------------------------------------
// Shared game types & constants
// ---------------------------------------------------------------------------

export type Tier = 'FREE' | 'PREMIUM' | 'VIP';
export type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';
export type Category =
  | 'ANCIENT'
  | 'MEDIEVAL'
  | 'MODERN'
  | 'WORLD_WARS'
  | 'SCIENCE_HISTORY'
  | 'POP_CULTURE_HISTORY'
  | 'EXPLORATION_AGE'
  | 'MYTHOLOGY'
  | 'RENAISSANCE'
  | 'ART_HISTORY'
  | 'ROMAN_HISTORY';

export type ChallengeType =
  | 'bounce'
  | 'mirror'
  | 'fade'
  | 'rotate'
  | 'shrink'
  | 'scramble'
  | 'invisible'
  | 'moving';

export type GameMode = 'STANDARD' | 'DAILY_CHALLENGE';

// ---------------------------------------------------------------------------
// Question
// ---------------------------------------------------------------------------
// NOTE: `correctAnswer` is intentionally absent. The answer key lives only on
// the backend; correctness is resolved via POST /api/check-answer so it can
// never be read from the client bundle or network response.
export interface Question {
  id: number;
  question: string;
  options: string[];
  tier: Tier;
  difficulty: Difficulty;
  category: Category;
  challengeType?: ChallengeType;
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------
export interface MultiplierBreakdown {
  base: number;
  speedMultiplier: number;
  streakMultiplier: number;
  tierMultiplier: number;
  total: number;
}

export interface SpeedBonusTier {
  maxSeconds: number;
  multiplier: number;
}

// ---------------------------------------------------------------------------
// Power-ups
// ---------------------------------------------------------------------------
export interface PowerUpState {
  fiftyFifty: number;
  timeFreeze: number;
  skip: number;
}

// ---------------------------------------------------------------------------
// Game session
// ---------------------------------------------------------------------------
export interface GameSession {
  id: string;
  mode: GameMode;
  tier: Tier;
  startedAt: number;
  endedAt: number | null;
  totalQuestions: number;
  correctAnswers: number;
  totalPoints: number;
  maxStreak: number;
  accuracy: number;
  heartsRemaining: number;
  questionsAnswered: { questionId: number; correct: boolean; timeMs: number }[];
}

// ---------------------------------------------------------------------------
// Daily challenge
// ---------------------------------------------------------------------------
export interface DailyChallenge {
  date: string; // YYYY-MM-DD
  seed: number;
  questionIds: number[];
}

// ---------------------------------------------------------------------------
// Login streak
// ---------------------------------------------------------------------------
export interface LoginStreak {
  currentStreak: number;
  longestStreak: number;
  lastLoginDate: string; // YYYY-MM-DD
}

// ---------------------------------------------------------------------------
// Player profile (designed for future Supabase migration)
// ---------------------------------------------------------------------------
export interface PlayerProfile {
  address: string;
  tier: Tier;
  totalPoints: number;
  gamesPlayed: number;
  bestScore: number;
  bestStreak: number;
  createdAt: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const TIMER_BY_DIFFICULTY: Record<Difficulty, number> = {
  EASY: 8,
  MEDIUM: 6,
  HARD: 4,
};

export const SPEED_BONUS_TIERS: SpeedBonusTier[] = [
  { maxSeconds: 1, multiplier: 3 },
  { maxSeconds: 2, multiplier: 2.5 },
  { maxSeconds: 3, multiplier: 2 },
  { maxSeconds: 4, multiplier: 1.5 },
];

export const STREAK_MULTIPLIER_TIERS = [
  { minStreak: 10, multiplier: 3 },
  { minStreak: 5, multiplier: 2 },
  { minStreak: 3, multiplier: 1.5 },
];

export const TIER_MULTIPLIERS: Record<Tier, number> = {
  FREE: 1,
  PREMIUM: 1.5,
  VIP: 3,
};

export const POWER_UP_ALLOWANCES: Record<Tier, PowerUpState> = {
  FREE: { fiftyFifty: 1, timeFreeze: 1, skip: 1 },
  PREMIUM: { fiftyFifty: 2, timeFreeze: 2, skip: 2 },
  VIP: { fiftyFifty: 3, timeFreeze: 3, skip: 3 },
};

export const BASE_POINTS = 100;
export const STANDARD_QUESTION_COUNT = 25;
export const DAILY_QUESTION_COUNT = 10;
